import moment from 'moment';
/**
 * Type for validators array
 */
// tslint:disable-next-line:max-line-length
export type Validators<FormData> = {
    [key in keyof Partial<FormData>]: Array<(config: { [key: string]: any }) => string | undefined>;
};

/**
 * Check if value is alphanumeric
 *
 * @param {{message?: string}} config
 * @returns {(value: string) => string}
 */
export const alphaNumeric = (config: { message?: string } = {}) => (value: any): string | undefined => {
    const { message } = {
        message: 'Only alphanumeric characters',
        ...config,
    };

    return value && /[^a-zA-Z0-9 ]/i.test(value) ? message : undefined;
};

/**
 * Check if value is numeric
 *
 * @param {{message?: string}} config
 * @returns {(value: (string | number)) => string}
 */
export const numeric = (config: { message?: string } = {}) => (value: any): string | undefined => {
    const { message } = {
        message: 'Must be a number',
        ...config,
    };

    return value && isNaN(Number(value)) ? message : undefined;
};

/**
 * Check value's max length
 *
 * @param {{max?: number; message?: string}} config
 * @returns {(value: string) => string}
 */
export const maxLength = (config: { max?: number; message?: string } = {}) => (value: any): string | undefined => {
    const { max, message } = {
        max: 10,
        message: 'Must be {max} characters or less',
        ...config,
    };

    return value && value.length > max ? message.replace('{max}', max.toString()) : undefined;
};

/**
 * Check value's min length
 *
 * @param {{min?: number; message?: string}} config
 * @returns {(value: string) => string}
 */
export const minLength = (config: { min?: number; message?: string } = {}) => (value: any): string | undefined => {
    const { min, message } = {
        min: 6,
        message: 'Must be {min} characters or more',
        ...config,
    };

    return value && value.length < min ? message.replace('{min}', min.toString()) : undefined;
};
export const minPhoneLength = (config: { min?: number; message?: string } = {}) => (value: any): string | undefined => {
    if (!value) {
        return value;
    }
    const checkVal = value.replace(/_/g, '');
    const { min, message } = {
        min: 6,
        message: 'Must be {min} characters or more',
        ...config,
    };

    return checkVal && checkVal.length < min ? message.replace('{min}', min.toString()) : undefined;
};
/**
 * Check if value less then maximum one
 *
 * @param {{max?: number; message?: string}} config
 * @returns {(value: number) => string}
 */
export const maxValue = (config: { max?: number; message?: string } = {}) => (value: any): string | undefined => {
    const { max, message } = {
        max: 5,
        message: 'Must be lower then {max}',
        ...config,
    };

    return value && value > max ? message.replace('{max}', max.toString()) : undefined;
};

/**
 * Check if value greater than minimal one
 *
 * @param {{min?: number; message?: string}} config
 * @returns {(value: number) => string}
 */
export const minValue = (config: { min?: number; message?: string } = {}) => (value: any): string | undefined => {
    const { min, message } = {
        min: 1,
        message: 'Must be at least {min}',
        ...config,
    };

    return value && value < min ? message.replace('{min}', min.toString()) : undefined;
};

/**
 * Check if value not empty
 *
 * @param {{message?: string}} config
 * @returns {(value: any) => string}
 */
export const required = (config: { message?: string } = {}) => (value: any): string | undefined => {
    const { message } = {
        message: 'Field is required',
        ...config,
    };

    return !value && 0 !== value ? message : undefined;
};

/**
 * Check if value is email address
 *
 * @param {{message?: string}} config
 * @returns {(value: any) => string}
 */
export const email = (config: { message?: string } = {}) => (value: any): string | undefined => {
    const { message } = {
        message: 'Invalid email address',
        ...config,
    };

    return value && !/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,4}$/i.test(value) ? message : undefined;
};
export const emailList = (config: { message?: string } = {}) => (value: any): string | undefined => {
    const { message } = {
        message: 'Invalid email address',
        ...config,
    };
    if (!value) {
        return value;
    }
    const arrEmail = value.split(';');
    let error = false;
    arrEmail.forEach((itEmail: string) => {
        if (!/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,4}$/i.test(itEmail)) {
            error = true;
        }
    });
    return error ? message : undefined;
};
export const phone = (config: { message?: string } = {}) => (value: any): string | undefined => {
    const { message } = {
        message: 'Invalid phone number',
        ...config,
    };

    return value && !/^\(?([0-9]{3})\)?[-. ]?([0-9]{3})[-. ]?([0-9]{4})$/.test(value) ? message : undefined;
};
/**
 * Check if value is email address
 *
 * @param {{message?: string}} config
 * @returns {(value: any) => string}
 */
export const calendar = (config: { message?: string }) => (value: any = {}) => {
    const { message } = {
        message: 'Your should choose correct dates range',
        ...config,
    };

    const { year, week } = value;

    return year && week ? undefined : message;
};
/**
 * Check that a date value is not earlier than another field's date value.
 * Compared by day (time part ignored). `field` is the name of the sibling form field.
 *
 * @param {{field?: string; message?: string}} config
 * @returns {(value: any, allValues?: any) => string | undefined}
 */
export const minDate = (config: { field?: string; message?: string } = {}) => (
    value: any,
    allValues?: any,
): string | undefined => {
    const { field, message } = {
        field: '',
        message: 'Date is out of the allowed range',
        ...config,
    };

    if (!value || !field || !allValues || !allValues[field]) {
        return undefined;
    }

    return moment(value).isBefore(moment(allValues[field]), 'day') ? message : undefined;
};

/**
 * Check that a date value is not later than another field's date value.
 * Compared by day (time part ignored). `field` is the name of the sibling form field.
 *
 * @param {{field?: string; message?: string}} config
 * @returns {(value: any, allValues?: any) => string | undefined}
 */
export const maxDate = (config: { field?: string; message?: string } = {}) => (
    value: any,
    allValues?: any,
): string | undefined => {
    const { field, message } = {
        field: '',
        message: 'Date is out of the allowed range',
        ...config,
    };

    if (!value || !field || !allValues || !allValues[field]) {
        return undefined;
    }

    return moment(value).isAfter(moment(allValues[field]), 'day') ? message : undefined;
};

export const noFuture = (config: { message?: string }) => (value: any = {}) => {
    const { message } = {
        message: 'Future date cannot be entered!',
        ...config,
    };

    //const { year, week } = value;

    return moment().diff(moment(value)) > 0 ? undefined : message;
};

/**
 * Check credit card number format
 *
 * @param {{message?: string}} config
 * @returns {(value: string) => string}
 */
export const cardNumberFormat = (config: { message?: string } = {}) => (value: any): string | undefined => {
    if (!value) return undefined;
    const cleaned = value.replace(/\s+/g, '');

    if (!/^\d{16}$/.test(cleaned)) {
        return 'Card number must be 16 digits';
    }

    return undefined;
};

/**
 * Check credit expired card number format
 *
 * @param {{message?: string}} config
 * @returns {(value: string) => string}
 */
export const expirationDateFormat = (config: { message?: string } = {}) => (value: any): string | undefined => {
    if (!value) return undefined;

    const match = value.match(/^(\d{2})\/(\d{2})$/);
    if (!match) return 'Invalid format, use MM/YY';

    const [_, month, year] = match;
    const monthNum = parseInt(month, 10);
    if (monthNum < 1 || monthNum > 12) return 'Invalid month';

    return undefined;
};

/**
 * CVV code format
 *
 * @param {{message?: string}} config
 * @returns {(value: string) => string}
 */
export const cvvValidator = (config: { message?: string } = {}) => (value: any): string | undefined => {
    if (!value) return 'Required';
    if (!/^\d{3,4}$/.test(value)) return 'CVV must be 3 or 4 digits';
    return undefined;
};
